import { EScreenType, IScreenDesignDto, IScreenDesignInfo, RightPanelType } from "@kuika/kuika-cl-model";
import { Rule } from "antd/es/form";
import * as React from "react";
import { ScreenService } from "../../../services/screen-service";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { SaveStatus } from "../../../shared/utility/save-status";
import { type IKuikaButtonStatusLabels, type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import { KuikaNameModal } from "../../ui-kit/kuika-name-modal/kuika-name-modal";

/**
 * "Create a blank report", wherever it is asked for: the landing page's card and the sub-bar's
 * "+" open the same sheet. A report is one name and nothing else — there is no template to
 * choose — so asking for it never has to close the report you are editing.
 *
 * A plain component rather than a `PureSdComponent`: what it renders depends on `visible` and
 * on its own progress, and `shouldComponentUpdate` there can swallow a prop-only change.
 */

/** Report names are screen names: start with a letter, then letters, digits or spaces. */
const REPORT_NAME_PATTERN: RegExp = /^[A-Za-z][A-Za-z0-9 ]+$/;

/** The confirm says what it is doing in its own words: a report is created, not saved. */
const CREATE_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Creating", success: "Created" };

export interface IReportCreateModalProps {
  visible: boolean;
  /** Cancelled, or the report has been created and is about to open. */
  onClose: () => void;
  /** For a host that shows progress of its own while the report is created. */
  onBusyChange?: (isBusy: boolean) => void;
}

export interface IReportCreateModalState {
  /** Where the create stands. Owned by `SaveStatus`, never set directly. */
  createStatus: KuikaButtonStatus;
}

class ReportCreateModal extends React.Component<IReportCreateModalProps & IWithSDProps, IReportCreateModalState> {
  /**
   * The create's state machine. The sheet stays up through the create and holds its ✓; only then
   * does it close and the new report open. `canStart` keeps an Enter pressed during the ✓ from
   * creating a second report.
   */
  private readonly createStatus: SaveStatus = new SaveStatus((createStatus: KuikaButtonStatus): void => {
    this.setState({ createStatus });
    this.props.onBusyChange?.(createStatus === "loading");
  });

  constructor(props: IReportCreateModalProps & IWithSDProps) {
    super(props);
    this.state = { createStatus: "idle" };
  }

  componentWillUnmount(): void {
    this.createStatus.dispose();
  }

  /**
   * The collision rules as an inline validator: the message lands on the field that caused it.
   * The table check matters because `renameCurrentScreen` rejects table names — without it a
   * report could be created with a name it could never be renamed back to.
   */
  private validateReportName = async (_rule: unknown, value: string): Promise<void> => {
    if (!value) return;
    const name: string = value.toLowerCase();
    const sd = this.props.sd;

    if (sd?.datasources?.find((ds) => ds.name.toLowerCase() === name)) {
      throw new Error("Report name can not be equal to the datasource name.");
    }
    if (sd?.workFlowList?.find((wf) => wf.name.toLowerCase() === name)) {
      throw new Error("Report name can not be equal to the process name.");
    }
    if (sd?.screenList?.find((screen: IScreenDesignInfo) => screen.name.toLowerCase() === name)) {
      throw new Error("Report name can not be equal to the screen name.");
    }
    const isEqualToTableName = Array.from(sd?.schemas?.values() ?? []).find((schema) =>
      schema?.tables.find((table) => table.name.toLowerCase() === name)
    );
    if (isEqualToTableName) {
      throw new Error("Report name can not be equal to the table name.");
    }
  };

  private getNameRules = (): Rule[] => [
    { required: true, message: "Please enter a name" },
    { pattern: REPORT_NAME_PATTERN, message: "Report name must contains A-Z or a-z or 0-9 or space" },
    { validator: this.validateReportName }
  ];

  /** The screen payload a report is created from — a single absolute-positioned root. */
  private buildReportPayload = (name: string): IScreenDesignDto => {
    const projectId: string = this.props.sd.project.id;
    const devices = {
      desktop: true,
      tablet: true,
      phone: true
    };
    const obj = {} as IScreenDesignDto;
    obj.name = name;
    obj.kProject_Id = projectId;
    obj.json =
      `{"projectId":"${projectId}","isAbsoluteDesign": true,"approveInfo":null,"id":"@SDID","name":"${name}","displayItems":[{"id":-1,"itemType":1,"item":{"id":"HTML_DIV","properties":[{"name":"style","selectedType":2,"value":""}]},"children":[],"screenDesign":"[Circular ~]"}],"versionNumber":0,"devices":${JSON.stringify(
        devices
      )},"authorization": { "permittedRoles": [], "allowAnonymousAccess": true, "allowAllRoles": true}` + `}`;
    obj.publicJson = JSON.stringify({ screenType: EScreenType.Report });
    return obj;
  };

  /**
   * `saveScreen` first: `loadScreen` navigates and reopens the project, so whatever is on the
   * canvas — the report you asked for this from — would otherwise lose its unsaved work. Both run
   * before the ✓; the sheet closes and the new report opens after it.
   */
  private createReport = async (name: string): Promise<void> => {
    if (!this.createStatus.canStart) return;
    this.createStatus.start();
    try {
      const res = await ScreenService.Create(this.buildReportPayload(name));
      if (!res || res.status !== 200) {
        this.createStatus.fail();
        return;
      }
      localStorage.setItem("isFreePositionAreaEnabled", "false");
      await this.props.sd.saveScreen(true);
      this.createStatus.succeed((): void => {
        this.props.onClose();
        this.props.sd.loadScreen(res.data);
        this.props.sd.setVisibilityOfCreateReportDesignPage(false);
        this.props.sd.setActiveRightPanelByClick(RightPanelType.Properties);
      });
    } catch (error) {
      this.createStatus.fail();
      KMainFunctions.exceptionHandler(error);
    }
  };

  private handleSubmit = (name: string): void => {
    void this.createReport(name);
  };

  render() {
    return (
      <KuikaNameModal
        visible={this.props.visible}
        title="Create a blank report"
        subtitle="Reports are designed on paper-sized pages and can be sent or downloaded."
        confirmLabel="Create"
        status={this.state.createStatus}
        statusLabels={CREATE_STATUS_LABELS}
        placeholder="Enter report name"
        rules={this.getNameRules()}
        inputTestId="reportDesignerNameInput"
        confirmTestId="reportDesignerCreateButton"
        className="kuika-report-name-modal"
        onCancel={this.props.onClose}
        onSubmit={this.handleSubmit}
      />
    );
  }
}

const reportCreateModal = withSD<IReportCreateModalProps>(ReportCreateModal);

export { reportCreateModal as ReportCreateModal };
